import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { tenantContextService } from '../services/api';
import { MIN_PASSWORD_LENGTH, PASSWORD_MIN_LENGTH_MESSAGE, PASSWORD_PLACEHOLDER } from '../utils/passwordPolicy';
import '../styles/Auth.css';

const normalizeCpf = (value) => String(value || '').replace(/\D/g, '');

const mapContextError = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return 'Ambiente nao encontrado para este link.';
  }
  return error?.response?.data?.detail || 'Nao foi possivel carregar o contexto do ambiente.';
};

const mapSignupError = (error) => {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || '').trim();

  if (status === 409 && detail) return detail;
  if (status === 403 && detail) return detail;
  if (status === 422) return 'Dados invalidos. Revise os campos e tente novamente.';
  if (detail) return detail;

  return 'Nao foi possivel concluir o cadastro neste ambiente.';
};

const TenantContextSignupPage = () => {
  const { tenant_slug: tenantSlugParam } = useParams();
  const tenantSlug = String(tenantSlugParam || '').trim();
  const navigate = useNavigate();

  const [loadingContext, setLoadingContext] = useState(true);
  const [tenantContext, setTenantContext] = useState(null);
  const [contextError, setContextError] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmar_senha: '',
    aceitar_termos: false,
    cpf: '',
    telefone: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let active = true;

    const loadContext = async () => {
      setLoadingContext(true);
      setContextError('');
      setTenantContext(null);

      try {
        const response = await tenantContextService.getTenantContext(tenantSlug);
        if (!active) return;

        const payload = response?.data || null;
        setTenantContext(payload);

        const tenantId = String(payload?.tenant_id || '').trim();
        if (tenantId) {
          localStorage.setItem('current_tenant_id', tenantId);
          localStorage.removeItem('tenant_id');
          localStorage.removeItem('selected_tenant_id');
        }
      } catch (requestError) {
        if (!active) return;
        setContextError(mapContextError(requestError));
      } finally {
        if (active) {
          setLoadingContext(false);
        }
      }
    };

    if (!tenantSlug) {
      setLoadingContext(false);
      setContextError('Link de ambiente invalido.');
    } else {
      loadContext();
    }

    return () => {
      active = false;
    };
  }, [tenantSlug]);

  const canSignup = useMemo(() => Boolean(tenantContext?.can_signup), [tenantContext]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    if (!formData.nome || !formData.email || !formData.senha || !formData.confirmar_senha) {
      return 'Preencha os campos obrigatorios.';
    }
    if (formData.senha !== formData.confirmar_senha) {
      return 'As senhas nao coincidem.';
    }
    if (formData.senha.length < MIN_PASSWORD_LENGTH) {
      return PASSWORD_MIN_LENGTH_MESSAGE;
    }
    if (!formData.aceitar_termos) {
      return 'Voce precisa aceitar os termos para continuar.';
    }

    const cpf = normalizeCpf(formData.cpf);
    if (cpf && cpf.length !== 11) {
      return 'CPF deve conter 11 digitos.';
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!tenantContext?.tenant_id || !canSignup) {
      setError('Cadastro indisponivel para este ambiente no momento.');
      return;
    }

    const payload = {
      nome: String(formData.nome || '').trim(),
      email: String(formData.email || '').trim().toLowerCase(),
      senha: formData.senha,
      confirmar_senha: formData.confirmar_senha,
      aceitar_termos: Boolean(formData.aceitar_termos),
      cpf: normalizeCpf(formData.cpf) || null,
      telefone: String(formData.telefone || '').trim() || null,
    };

    setLoading(true);
    try {
      await tenantContextService.signupInTenantContext(tenantSlug, payload);
      setSuccess('Cadastro concluido com sucesso. Voce ja pode acessar este ambiente pelo login contextual.');
      setTimeout(() => {
        navigate(`/t/${tenantSlug}/login`);
      }, 1800);
    } catch (requestError) {
      setError(mapSignupError(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card cadastro-card">
        <div className="auth-header">
          <h1>Cadastro no Ambiente</h1>
          <p>{tenantContext?.display_name || 'Carregando ambiente...'}</p>
        </div>

        {loadingContext ? <div className="alert alert-success">Carregando contexto do ambiente...</div> : null}
        {contextError ? <div className="alert alert-error">{contextError}</div> : null}
        {!loadingContext && !contextError && !canSignup ? (
          <div className="alert alert-error">Cadastro indisponivel para este ambiente no momento.</div>
        ) : null}

        {error ? <div className="alert alert-error">{error}</div> : null}
        {success ? <div className="alert alert-success">{success}</div> : null}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="nome">Nome</label>
            <input
              id="nome"
              name="nome"
              type="text"
              value={formData.nome}
              onChange={handleChange}
              placeholder="Seu nome completo"
              required
              disabled={loading || loadingContext || !!contextError || !canSignup}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="seu@email.com"
              required
              disabled={loading || loadingContext || !!contextError || !canSignup}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cpf">CPF (opcional)</label>
            <input
              id="cpf"
              name="cpf"
              type="text"
              value={formData.cpf}
              onChange={(event) => {
                const value = normalizeCpf(event.target.value).slice(0, 11);
                setFormData((prev) => ({ ...prev, cpf: value }));
              }}
              placeholder="00000000000"
              maxLength={11}
              disabled={loading || loadingContext || !!contextError || !canSignup}
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefone">Telefone (opcional)</label>
            <input
              id="telefone"
              name="telefone"
              type="text"
              value={formData.telefone}
              onChange={handleChange}
              placeholder="(00) 00000-0000"
              disabled={loading || loadingContext || !!contextError || !canSignup}
            />
          </div>

          <div className="form-group">
            <label htmlFor="senha">Senha</label>
            <div className="password-input-group">
              <input
                id="senha"
                name="senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={handleChange}
                placeholder={PASSWORD_PLACEHOLDER}
                required
                disabled={loading || loadingContext || !!contextError || !canSignup}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={loading || loadingContext || !!contextError || !canSignup}
              >
                {showPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmar_senha">Confirmar senha</label>
            <input
              id="confirmar_senha"
              name="confirmar_senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmar_senha}
              onChange={handleChange}
              placeholder="Repita a senha"
              required
              disabled={loading || loadingContext || !!contextError || !canSignup}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                name="aceitar_termos"
                type="checkbox"
                checked={formData.aceitar_termos}
                onChange={handleChange}
                disabled={loading || loadingContext || !!contextError || !canSignup}
              />
              Aceito os termos de uso deste ambiente.
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || loadingContext || !!contextError || !canSignup}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>

        <div className="auth-links">
          <span>Ja possui acesso?</span>
          <Link to={`/t/${tenantSlug}/login`} className="link">
            Entrar neste ambiente
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TenantContextSignupPage;
